import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import styles from '../TournamentWidget.module.scss';

import { ChampionsActiveRoundWidgetTrophy } from './ChampionsActiveRoundWidgetTrophy';
import { ChampionsBetweenRoundsWidgetTrophy } from './ChampionsBetweenRoundsWidgetTrophy';
import { CHAMPIONS_WIDGET_TROPHY_MODE, type ChampionsWidgetTrophyPresentation } from './championsWidgetTrophyMode';

const CROSSFADE_MS = 320;

type LayerSnapshot = ChampionsWidgetTrophyPresentation;

type ChampionsWidgetTrophyCrossfadeProps = {
  presentation: ChampionsWidgetTrophyPresentation;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
};

function renderTrophyLayer(
  layer: LayerSnapshot,
  className: string | undefined,
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void,
) {
  if (layer.mode === CHAMPIONS_WIDGET_TROPHY_MODE.BETWEEN_ROUNDS) {
    return <ChampionsBetweenRoundsWidgetTrophy className={className} src={layer.src} onImageError={onImageError} />;
  }

  return (
    <ChampionsActiveRoundWidgetTrophy
      className={className}
      isOnFinalRound={layer.isOnFinalRound}
      src={layer.src}
      variant={layer.variant}
      onImageError={onImageError}
    />
  );
}

function hasPresentationChanged(prev: LayerSnapshot, next: LayerSnapshot): boolean {
  return (
    prev.mode !== next.mode ||
    prev.src !== next.src ||
    prev.variant !== next.variant ||
    prev.isOnFinalRound !== next.isOnFinalRound
  );
}

function shouldCrossfade(prev: LayerSnapshot, next: LayerSnapshot): boolean {
  return prev.mode !== next.mode || prev.src !== next.src;
}

export function ChampionsWidgetTrophyCrossfade({ presentation, onImageError }: ChampionsWidgetTrophyCrossfadeProps) {
  const [visiblePresentation, setVisiblePresentation] = useState(presentation);
  const [outgoing, setOutgoing] = useState<LayerSnapshot | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPresentationRef = useRef<LayerSnapshot>(presentation);

  useEffect(() => {
    const prev = prevPresentationRef.current;

    if (!hasPresentationChanged(prev, presentation)) return;

    if (shouldCrossfade(prev, presentation)) {
      setOutgoing(prev);
      setVisiblePresentation(presentation);
    } else {
      setVisiblePresentation(presentation);
    }

    prevPresentationRef.current = presentation;

    if (!shouldCrossfade(prev, presentation)) return;

    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setOutgoing(null);
      fadeTimerRef.current = null;
    }, CROSSFADE_MS);

    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [presentation]);

  const frameClassName = classNames(
    styles.trophyIconFrame,
    presentation.isCountdown && styles.trophyIconFrameCountdown,
  );

  return (
    <div className={frameClassName}>
      {outgoing ? renderTrophyLayer(outgoing, styles.trophyIconLayerFadeOut, onImageError) : null}
      {renderTrophyLayer(visiblePresentation, outgoing ? styles.trophyIconLayerFadeIn : undefined, onImageError)}
    </div>
  );
}
